import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import swal from "sweetalert";
import { printInfoMessage, routeConstants } from "../../../../shared/constants";
import { selectedFiltersAtom, selectedPlaceAtom, selectedSpatialLayerAtom, smallBusinessMapColourOnAtom, smallBusinessMapHighlightAtom, smallBusinessMapZoomInAtom, yearsRangeAtom } from "../../../../shared/state/atoms";
import FirmSizeRangeSlider from "../../../../shared/components/FirmSizeRangeSlider";

// Filter for Small Business / Map of Intensity (Phase B5).
// - Year toggle (uses global selectedFiltersAtom.singleYear, matching every other dashboard)
// - 1-handle FirmSizeRangeSlider for the small-business range
// - Print Current + Print All actions (NO Download Data — map dashboards in this app do not
//   expose a data export, matching Job Density and Change in Metro)
function SmallBusinessMapIntensityFilter() {
  const [yearsRange] = useAtom(yearsRangeAtom);
  const [selectedFilters, setSelectedFilters] = useAtom(selectedFiltersAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [zoomIn, setZoomIn] = useAtom(smallBusinessMapZoomInAtom);
  const [highlightOn, setHighlightOn] = useAtom(smallBusinessMapHighlightAtom);
  const [colourOn, setColourOn] = useAtom(smallBusinessMapColourOnAtom);
  const navigate = useNavigate();

  // Zoom toggle only applies to municipality and district views.
  // Hex/metro mode is already zoomed to the selected metro.
  // Province: no zoom per client revert — always show full country view.
  const isHexMode = selectedSpatialLayer?.id === "municipality" && selectedPlace?.category === "A";
  const isProvince = selectedSpatialLayer?.id === "province";
  const showZoomToggle = selectedSpatialLayer && selectedPlace && !isHexMode && !isProvince;

  useEffect(() => {
    if (!selectedFilters.singleYear || selectedFilters.singleYear < yearsRange[0]) {
      setSelectedFilters({
        ...selectedFilters,
        singleYear: yearsRange[yearsRange.length - 1],
      });
    }
  }, [selectedFilters, setSelectedFilters, yearsRange]);

  const handlePrintNavigation = async () => {
    await swal("Please Note", printInfoMessage, "info");
    window.open(`${routeConstants.PDF_PREVIEW}/smallBusinessMapIntensityPdf`, "_blank");
  };

  const handlePrintAllNavigation = async () => {
    await swal("Please Note", printInfoMessage, "info");
    navigate(`${routeConstants.PDF_PREVIEW}/all`);
  };

  return (
    <div className="w-full">
      <div>
        <div style={{ fontSize: "16px" }} className="font-bold pb-5">
          Toggles:
        </div>
        <div style={{ borderBottom: "1px solid lightgrey" }} className="flex justify-between mb-4 pb-4">
          <label>Year:</label>
          <select
            className="border"
            name="singleYear"
            value={selectedFilters?.singleYear ?? ""}
            onChange={(e) =>
              setSelectedFilters({
                ...selectedFilters,
                singleYear: e.target.value,
              })
            }
          >
            {yearsRange.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
          <label>Firm Size Range (FTE Employees):</label>
          <FirmSizeRangeSlider
            dashboardId="smallBusinessMapIntensity"
            numHandles={1}
            segmentLabels={["Small (included)", "Medium-Large (excluded)"]}
            segmentColors={["#424242", "#e0e0e0"]}
            allowFullRange
            invertible
          />
        </div>
        {selectedSpatialLayer && selectedPlace && (
          <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
            <div className="flex justify-between">
              <label className="form-check-label inline-block text-gray-800" htmlFor="colourToggle">
                Colour:
              </label>
              <div className="form-check form-switch">
                <input
                  checked={colourOn}
                  onChange={() => setColourOn(!colourOn)}
                  className="form-check-input appearance-none w-9 rounded-full h-5 bg-no-repeat bg-contain bg-gray-300 focus:outline-none cursor-pointer shadow-sm checked:bg-blue-500"
                  type="checkbox"
                  role="switch"
                  id="colourToggle"
                />
              </div>
            </div>
          </div>
        )}
        {(showZoomToggle || !isHexMode) && (
          <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
            {showZoomToggle && (
              <div className="flex justify-between mb-2">
                <label className="form-check-label inline-block text-gray-800" htmlFor="zoomToggle">
                  Zoom to Place:
                </label>
                <div className="form-check form-switch">
                  <input
                    checked={zoomIn}
                    onChange={() => setZoomIn(!zoomIn)}
                    className="form-check-input appearance-none w-9 rounded-full h-5 bg-no-repeat bg-contain bg-gray-300 focus:outline-none cursor-pointer shadow-sm checked:bg-blue-500"
                    type="checkbox"
                    role="switch"
                    id="zoomToggle"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-between">
              <label className="form-check-label inline-block text-gray-800" htmlFor="highlightToggle">
                Highlight Place:
              </label>
              <div className="form-check form-switch">
                <input
                  checked={highlightOn}
                  onChange={() => setHighlightOn(!highlightOn)}
                  className="form-check-input appearance-none w-9 rounded-full h-5 bg-no-repeat bg-contain bg-gray-300 focus:outline-none cursor-pointer shadow-sm checked:bg-red-500"
                  type="checkbox"
                  role="switch"
                  id="highlightToggle"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: "30px", borderBottom: "1px solid lightgrey", flexDirection: "column" }} className="flex justify-between mb-4 pb-8">
        <div style={{ fontSize: "16px" }} className="font-bold pb-5">
          Actions:
        </div>
        <div style={{ flexDirection: "row" }}>
          <button
            style={{
              border: "2px solid #F0A400",
              borderRadius: "50px",
              padding: "8px",
              paddingLeft: "14px",
              color: "#F0A400",
              width: "100%",
              marginBottom: "10px",
            }}
            className="font-sans"
            onClick={handlePrintNavigation}
          >
            Print Current Dashboard
          </button>
          <button
            style={{
              border: "2px solid #F0A400",
              borderRadius: "50px",
              padding: "8px",
              paddingLeft: "14px",
              color: "#F0A400",
              width: "100%",
            }}
            className="font-sans"
            onClick={handlePrintAllNavigation}
          >
            Print All Dashboards
          </button>
        </div>
      </div>
    </div>
  );
}

export default SmallBusinessMapIntensityFilter;
